import React, { useEffect, useState } from 'react';

import { PaymentFormContainer } from './PaymentForm.styled';
import { StripeContext } from 'components/CreditCardForm/types/creditCardForm';
import { usePromo } from 'hooks/usePromo';
import { AppointmentProduct, AppointmentType, ValidPromo } from 'kb-shared';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { PromoCode } from 'screens/Book/components/PromoCode/PromoCode';
import { CreditCardDetails } from 'screens/Invoices/components/InvoicePayModal/CreditCardDetails';
import { analytics } from 'utilities/analytics';
import { showErrorToast, showSuccessToast } from 'utilities/notificationUtils';

import { CancellationInfo } from './CancellationInfo';

interface Props {
  loading?: boolean;
  noPayment?: () => void;
  onPaymentOptional?: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onValidPromoChange: (validPromo: ValidPromo | null) => void;
  product: AppointmentProduct<AppointmentType>;
  hasEmployer?: boolean;
  onStripeTokenLoading: (isLoading: boolean) => void;
  setStripeContext: (data: StripeContext | undefined) => void;
  setKbStripeCardStripeIdentifier: (stripeIdentifier: string) => void;
  setIsCreditCardFieldEmpty: (isEmpty: boolean) => void;
  setIsInputValid: (isValid: boolean) => void;
}

export const PaymentForm = (props: Props) => {
  const {
    noPayment,
    onPaymentOptional,
    onValidPromoChange,
    product,
    setStripeContext,
    setKbStripeCardStripeIdentifier,
    setIsCreditCardFieldEmpty,
    setIsInputValid
  } = props;
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState(false);
  const [inputValid, setInputValid] = useState(false);
  const { validatePromo } = usePromo(product);

  useEffect(() => {
    analytics.track(analytics.EVENTS.PATIENT_PORTAL_BOOKING_APPOINTMENT_PAYMENT_LOADED);
  }, []);

  useEffect(() => {
    setIsInputValid(inputValid);
  }, [inputValid]);

  // clear promo error when promo code is changed
  useEffect(() => {
    if (!promoCode) setPromoError(false);
  }, [promoCode]);

  return (
    <form>
      {!noPayment && (
        <PaymentFormContainer>
          <CreditCardDetails
            paymentOptional={Boolean(onPaymentOptional)}
            onSelectCreditCard={data => {
              setKbStripeCardStripeIdentifier(data);
            }}
            onStripeCardElementInitialized={(stripe, cardNumberElement) => {
              setStripeContext({
                stripe,
                cardNumberElement
              });
            }}
            onStripeElementUnmount={() => setStripeContext(undefined)}
            onValidation={valid => {
              if (valid) analytics.track(analytics.EVENTS.CREDIT_CARD_ADDED);

              setInputValid(valid);
            }}
            onCreditCardChange={({ empty }) => setIsCreditCardFieldEmpty(empty)}
          >
            <PromoCode
              value={promoCode || ''}
              error={promoError}
              onChange={value => setPromoCode(value)}
              onSubmit={() => {
                setPromoError(false);
                validatePromo(promoCode)
                  .then(({ validPromo, error }) => {
                    onValidPromoChange(validPromo || null);
                    if (error) {
                      setPromoError(true);
                      analytics.track(analytics.EVENTS.PROMO_CODE_FAILED, {
                        promo_code: promoCode
                      });
                      return showErrorToast(error);
                    }
                    showSuccessToast('Promo code accepted.');
                    analytics.track(analytics.EVENTS.PROMO_CODE_SUCCEEDED, {
                      promo_code: promoCode
                    });
                  })
                  .catch(error => BugTracker.notify(error, 'Validate promo code'));
              }}
            />
          </CreditCardDetails>
        </PaymentFormContainer>
      )}
      <CancellationInfo />
    </form>
  );
};
